import {
    IAssertionDescriptorModel,
    IAssertionDescriptorValueModel,
} from '../interfaces/IAssertionDescriptor';
import { ICaseAssertionModel, ICaseModel, ICaseStatusLookupModel } from '../interfaces/ICase';

import { AssertionDescriptorValueSourceTypeEnum } from './Constants';
import { IMultiSelectOption } from '../interfaces/ILookup';
import { cloneDeep } from 'lodash';

export default class CaseFileCheckmateSelectHelper {
    static isSearchableSelect(name?: string): boolean {
        if (name) {
            switch (name.toUpperCase()) {
                case 'PLAINTIFFS FIRM - LOCAL':
                case 'PLAINTIFFS FIRM - SECONDARY':
                case 'JURISDICTION':
                case 'OCCUPATION':
                    return true;
                default:
                    return false;
            }
        }

        return false;
    }

    static getCaseStatusOptionsForCheckmateSelect(values: ICaseStatusLookupModel[]) {
        const options: IMultiSelectOption[] = [];
        for (let i = 0; i < values.length; i++) {
            options.push({ label: values[i].name!, value: values[i].name!, id: values[i].id });
        }
        return options;
    }

    static getCaseStatusValue(values: ICaseStatusLookupModel[], id: number) {
        const match = values.find((x) => x.id === id);
        if (match) {
            const selectedValue: IMultiSelectOption = {
                label: match.name!,
                value: match.name!,
                id: match.id,
            };
            return selectedValue;
        }
    }

    static getOptionsForCheckmateSelect(
        values: IAssertionDescriptorValueModel[]
    ): IMultiSelectOption[] {
        return values.map((value, i) => ({
            label: value.text!,
            value: value.guid!,
            id: i,
            guid: value.guid!,
        }));
    }

    static getCheckmateSelectValue(
        caseAssertion: ICaseAssertionModel,
        assertionDescriptors: IAssertionDescriptorModel[]
    ) {
        const foundAD = assertionDescriptors.find(
            (ad) => ad.guid === caseAssertion.assertionDescriptorGuid
        );

        const foundADV = foundAD?.assertionDescriptorValues?.find(
            (x) => x.guid === caseAssertion?.assertionDescriptorValueGuid
        );

        if (foundADV) {
            const selectedValue: IMultiSelectOption = {
                label: foundADV.text!,
                value: foundADV.guid!,
                id: 0,
                guid: foundADV.guid!,
            };
            return selectedValue;
        }
    }

    static getCheckmateNSelectValue(
        caseAssertions: ICaseAssertionModel[],
        assertionDescriptors: IAssertionDescriptorModel[]
    ): IMultiSelectOption[] {
        const values = caseAssertions
            .filter(Boolean) // Removes undefined and null
            .map((ca) => {
                const descriptor = assertionDescriptors.find(
                    (ad) => ad.guid === ca.assertionDescriptorGuid
                );

                const value = descriptor?.assertionDescriptorValues?.find(
                    (adv) => adv.guid === ca.assertionDescriptorValueGuid
                );

                if (!value) return undefined;

                return {
                    label: value.text ?? '',
                    value: value.guid,
                    id: 0,
                    guid: value.guid,
                } as IMultiSelectOption;
            })
            .filter((item): item is IMultiSelectOption => item !== undefined);

        return values;
    }

    static updateCaseOnSelectedAssertionDescriptorValueChange(
        caseItem: ICaseModel,
        assertionDescriptorGuid: string,
        isBoolean: boolean,
        selectedItem?: IMultiSelectOption,
        assertionDescriptors?: IAssertionDescriptorModel[]
    ) {
        const caseItemCopy = cloneDeep(caseItem);
        const foundAD = assertionDescriptors?.find((x) => x.guid == assertionDescriptorGuid);

        // AR - TO DO - Do not trust the "isBoolean" value passed in, if you can determine from the assertionDescriptor, thats the best option!
        if (assertionDescriptorGuid && foundAD) {
            if (!caseItemCopy.caseAssertions) caseItemCopy.caseAssertions = [];

            const caseAssertionMatch = caseItemCopy.caseAssertions.find(
                (x) => x.assertionDescriptorGuid === assertionDescriptorGuid
            );

            if (caseAssertionMatch) {
                isBoolean =
                    foundAD.valueSourceType?.id ===
                    AssertionDescriptorValueSourceTypeEnum.Boolean.Value;

                if (isBoolean) {
                    caseAssertionMatch.assertionDescriptorValueGuid = undefined;
                    caseAssertionMatch.booleanValue = selectedItem
                        ? ['YES', 'TRUE'].includes(selectedItem.value?.toUpperCase())
                        : undefined;
                    caseAssertionMatch.userOverride = true;
                } else {
                    if (selectedItem?.guid) {
                        const advMatch = foundAD.assertionDescriptorValues!.find(
                            (x) => x.guid === selectedItem.guid
                        );

                        caseAssertionMatch.assertionDescriptorValueGuid = advMatch?.guid;
                    } else {
                        caseAssertionMatch.assertionDescriptorValueGuid = selectedItem?.guid;
                    }

                    caseAssertionMatch.booleanValue = undefined;
                    caseAssertionMatch.textValue = undefined;
                    caseAssertionMatch.userOverride = true;
                }
            } else if (selectedItem) {
                if (
                    foundAD.valueSourceType?.id ===
                    AssertionDescriptorValueSourceTypeEnum.Boolean.Value
                ) {
                    isBoolean = true;
                }

                if (isBoolean) {
                    const boolCaseAssertion = {
                        assertionDescriptorGuid: foundAD.guid,
                        booleanValue: selectedItem
                            ? ['YES', 'TRUE'].includes(selectedItem.value?.toUpperCase())
                            : undefined,
                        userOverride: true,
                    };

                    caseItemCopy.caseAssertions.push(boolCaseAssertion);
                } else {
                    const advMatch = foundAD.assertionDescriptorValues!.find(
                        (x) => x.guid === selectedItem.guid
                    );

                    caseItemCopy.caseAssertions.push({
                        assertionDescriptorGuid: foundAD.guid,
                        assertionDescriptorValueGuid: advMatch?.guid,
                        userOverride: true,
                    });
                }
            }
        }

        return caseItemCopy;
    }
}
